// Shared submit-button gate for the timesheet forms.
//
// More than one controller has a reason to stop a submission — the multi-site
// allocation not adding up to the day, a statutory break the employee still has to
// enter — and each used to write `disabled` on the buttons directly. That does not
// compose: whichever controller ran last won, so clearing one reason re-enabled a
// button the other still wanted disabled, and a controller disconnecting released a
// gate it never held.
//
// Instead each holds a NAMED gate. The buttons stay disabled while any gate is held,
// so the controllers never have to know about each other.
const GATES = new WeakMap()

// Every submit button of the form, not just the first: the offcanvas drawer renders
// its own alongside the inline one.
//
// The drawer's button is not always INSIDE the form — the Schnellerfassung renders
// its Speichern in the offcanvas footer and points a submit_button_controller at the
// form (data-submit-button-form-target). Those submit this form just the same, so a
// gate that only walks the form's own descendants cannot reach them: the button
// stays enabled, and clicking it hands the user a spinner that never resolves,
// because the holding controller's submit backstop cancels the submission anyway.
function submitButtons(form) {
  const own = [...form.querySelectorAll("input[type=submit], button[type=submit]")]
  const external = [...document.querySelectorAll("[data-submit-button-form-target]")].filter(
    (button) => {
      const selector = button.dataset.submitButtonFormTarget
      if (!selector || form.contains(button)) return false

      try {
        return document.querySelector(selector) === form
      } catch {
        return false
      }
    },
  )

  return [...own, ...external]
}

function apply(form) {
  const blocked = (GATES.get(form)?.size ?? 0) > 0
  submitButtons(form).forEach((button) => {
    button.disabled = blocked
    button.classList.toggle("opacity-50", blocked)
    button.classList.toggle("cursor-not-allowed", blocked)
  })
}

// Hold (blocked) or release (!blocked) this key's gate. Idempotent — safe to call on
// every keystroke.
export function setSubmitGate(form, key, blocked) {
  if (!form) return

  let keys = GATES.get(form)
  if (!keys) {
    keys = new Set()
    GATES.set(form, keys)
  }

  const held = keys.has(key)
  if (blocked === held) return

  if (blocked) keys.add(key)
  else keys.delete(key)
  apply(form)
}

// Whether any gate is currently held. Used by submit handlers as a backstop, since a
// disabled button is not the only way a form can be submitted.
export function submitGated(form) {
  return (GATES.get(form)?.size ?? 0) > 0
}
